import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSchedule, fmtLong, iso, SLOT_LABEL } from './useSchedule';
import { BlockModal } from './BlockModal';
import { CustomerTextModal } from './CustomerTextModal';
import { UpgradeModal } from '@/components/billing/UpgradeModal';
import { useMyPlan } from '@/hooks/useMyPlan';

export function ProjectScheduleTab({ project, readOnly }) {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const [textBlock, setTextBlock] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const { has } = useMyPlan();
  const textCustomer = Object.assign((id) => setTextBlock(id), { allowed: has('customer_sms') });
  const s = useSchedule({ from: '2000-01-01', to: '2100-01-01', projectId: project.id, installerId: readOnly ? project.installer_id : undefined });
  const sorted = [...s.blocks].sort((a, b) => a.start_date.localeCompare(b.start_date));
  const scheduledStageIds = new Set(sorted.map((b) => b.stage_id).filter(Boolean));
  const unscheduled = (project.stages || []).filter((st) => !scheduledStageIds.has(st.id));
  const section = ['probound', 'proepoxy', 'prodecor'].includes(project.service_type) ? project.service_type : null;

  return (
    <div className="space-y-4" data-testid="project-schedule-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Schedule</h2>
          <p className="text-sm text-gray-500">Which parts of this install happen on which days.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`${section ? `/${section}` : ''}/calendar${readOnly ? `?installer=${project.installer_id}` : ''}`)} data-testid="schedule-open-calendar"><CalendarDays className="w-4 h-4 mr-1.5" /> Full calendar</Button>
          {!readOnly && <Button size="sm" className="bg-[#006400] hover:bg-[#004d00] text-white" onClick={() => setModal({ start_date: project.install_date || iso(new Date()), project_id: project.id })} data-testid="schedule-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add block</Button>}
        </div>
      </div>

      {!readOnly && unscheduled.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="schedule-unscheduled">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1.5">Not scheduled yet</div>
          <div className="flex flex-wrap gap-1.5">
            {unscheduled.map((st) => (
              <button key={st.id} type="button" onClick={() => setModal({ start_date: project.install_date || iso(new Date()), project_id: project.id, stage_id: st.id, label: st.name })}
                className="text-xs font-semibold px-2.5 py-1.5 rounded-full border border-amber-300 bg-white hover:bg-amber-100" data-testid={`schedule-unscheduled-${st.id}`}>+ {st.name}</button>
            ))}
          </div>
        </div>
      )}

      {s.loading ? <p className="text-gray-500 text-sm">Loading…</p> : sorted.length === 0 ? (
        <div className="bg-white border border-dashed rounded-xl p-8 text-center text-gray-500 text-sm" data-testid="schedule-empty">Nothing on the calendar for this project yet.</div>
      ) : (
        <div className="bg-white border rounded-xl divide-y" data-testid="schedule-list">
          {sorted.map((b) => (
            <button key={b.id} type="button" disabled={readOnly} onClick={() => setModal(b)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:hover:bg-white" data-testid={`schedule-row-${b.id}`}>
              <div className="w-28 shrink-0">
                <div className="text-sm font-bold text-gray-900">{new Date(b.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                {b.end_date !== b.start_date && <div className="text-[11px] text-gray-500">→ {new Date(b.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{b.label}</div>
                <div className="text-xs text-gray-500 truncate">{b.slot === 'hours' ? `${b.start_time}–${b.end_time}` : SLOT_LABEL[b.slot]}{b.note ? ` · ${b.note}` : ''}</div>
              </div>
              <div className="flex gap-1">
                {(b.crew_ids || []).map((id) => s.crew.find((c) => c.id === id)).filter(Boolean).map((c) => (
                  <span key={c.id} className="text-[10px] font-bold text-white rounded px-1.5 py-0.5" style={{ background: c.colour }}>{c.name.split(' ')[0]}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-gray-400">{fmtLong(new Date())}</p>

      <BlockModal open={!!modal} initial={modal} projects={s.projects.length ? s.projects : [project]} crew={s.crew} lockProject
        onSave={(body) => (modal?.id ? s.updateBlock(modal.id, body) : s.createBlock(body))} onDelete={s.deleteBlock} onClose={() => setModal(null)}
        onTextCustomer={readOnly ? undefined : textCustomer} />
      <CustomerTextModal blockId={textBlock} onClose={() => setTextBlock(null)} onSent={s.reload} onUpgrade={() => { setTextBlock(null); setUpgrade('Texting customers their install dates'); }} />
      <UpgradeModal open={!!upgrade} feature={upgrade} onClose={() => setUpgrade(null)} />
    </div>
  );
}
